import AnimatedNumber from 'animated-number-react';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import { toast } from 'react-hot-toast';
import useSWR from 'swr';

import { buyCarCreateOrder } from '../../../api/services';
import { numberFormat } from '../../../helpers/formatters';
import PageModal from '../../PageModal';
import Modal from '../../Widgets/Modal';
import { useBuyRequestData } from '../BuyRequestProvider';

interface IProps {
  setStep(step: number): void;
}

const RequestType = ({ setStep }: IProps) => {
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [budgetModalVisible, setBudgetModalVisible] = useState<boolean>(false);
  const [freeOrderModalV, setFreeOrderModalV] = useState<boolean>(false);
  const { budget, setBudget, setOrder } = useBuyRequestData();
  const [budgetField, setBudgetField] = useState<string>('');

  const router = useRouter();

  const { data: freeOrderDescription, error: freeOrderError } = useSWR(`/wp/v2/pages/?slug=buy-car-free-order`);

  useEffect(() => {
    setBudgetField(budget ? numberFormat(budget) : '');
  }, [budget]);

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
      }}
      className="relative"
    >
      <h3 className="text-3xl mb-3 font-black text-center">نوع درخواست شما</h3>
      <p className="text-center mb-10 text-neutral-600 text-base">در این مرحله باید نوع درخواست خود را مشخص کنید</p>

      <h3 className="text-center text-xl font-bold mb-3">درخواست سفارشی</h3>
      <button
        type="button"
        className="button primary block w-full mb-4"
        onClick={() => {
          setBudgetModalVisible(true);
        }}
      >
        ثبت درخواست سفارشی
      </button>
      <p className="mb-10 text-center text-neutral-350">
        هزینه: ۱٪ مبلغ خودرو{' '}
        <button className="text-green-700 underline font-medium" type="button">
          مشاهده توضیحات
        </button>
      </p>

      <h3 className="text-center text-xl font-bold mb-3">درخواست عادی</h3>
      <button
        type="button"
        onClick={() => {
          setFreeOrderModalV(true);
        }}
        className="button small cancel block w-full mb-4"
      >
        ثبت درخواست عادی (رایگان و بدون تعهد)
      </button>
      <p className="mb-10 text-center text-neutral-350">
        بدون هزینه{' '}
        <button className="text-green-700 underline font-medium" type="button">
          مشاهده توضیحات
        </button>
      </p>

      <button type="button" onClick={() => setStep(1)} className="underline underline-offset-4 mx-auto table">
        بازگشت به مرحله قبل
      </button>

      {freeOrderModalV && (
        <Modal width="500px" animation="slide" title="درخواست خرید خودرو" toggle={setFreeOrderModalV} closable tapClose>
          <div className={`legal-stuff h-full flex flex-col`}>
            <label className="block text-center font-bold text-xl text-neutral-900 mb-1">
              ثبت سفارش خرید خودرو رایگان
            </label>

            <div className="flex-1 -mx-5 px-5 overflow-y-auto overflow-x-hidden mb-4">
              {freeOrderDescription.length > 0 && (
                <div
                  className="post-content text-base leading-7 font-light text-justify text-dark-900"
                  dangerouslySetInnerHTML={{ __html: freeOrderDescription[0]?.content?.rendered }}
                />
              )}
            </div>

            <button
              type="button"
              className="button primary block w-full mt-auto mb-4"
              onClick={() => {
                buyCarCreateOrder({ request_type: 'free' }).then(
                  ({ data }) => {
                    setSubmitting(false);

                    if (!data?.order?.needs_payment && data?.order?.success_uri) {
                      router.push(data.order.success_uri);
                    }
                  },
                  () => {
                    setSubmitting(false);
                  },
                );
              }}
            >
              ثبت سفارش
            </button>

            <button
              type="button"
              className="button cancel small block w-full"
              onClick={() => {
                setBudgetModalVisible(false);
              }}
            >
              انصراف
            </button>
          </div>
        </Modal>
      )}

      {budgetModalVisible && (
        <Modal
          width="500px"
          animation="slide"
          title="تعیین بودجه خرید خودرو"
          toggle={setBudgetModalVisible}
          closable
          tapClose
        >
          <div className={`legal-stuff h-full flex flex-col relative`}>
            {submitting && <div className="blockUI" />}
            <label className="block text-center font-bold text-xl text-neutral-900 mb-1">سقف بودجه شما (تومان)</label>

            <div className="primary-field mb-5">
              <input
                type="text"
                value={budgetField}
                inputMode="numeric"
                onChange={(e) => {
                  const value = parseFloat(e.target.value.replace(/\D/g, ''));
                  setBudget(value);
                  // setBudgetField(value ? numberFormat(value) : '');
                }}
                className="text-center no-icon tracking-widest appearance-none"
                placeholder=""
              />
            </div>

            <p className="text-center mb-4 flex items-center justify-center">
              <span className="text-lg text-neutral-600 ml-1">هزینه ثبت سفارش:‌</span>
              <strong className="text-green-400 font-black text-3xl">
                <AnimatedNumber value={budget && budget * 0.01} formatValue={numberFormat} delay={700} />
                <small className="text-2xl mr-1">تومان</small>
              </strong>
            </p>

            <PageModal
              render={(clickHandler) => (
                <button
                  onClick={clickHandler}
                  type="button"
                  className="table text-base underline font-bold underline-offset-4 text-neutral-350 mb-8 mx-auto"
                >
                  توضیحات نحوه محاسبه هزینه
                </button>
              )}
              slug="buy-car-cost-calculation"
            />

            <button
              type="button"
              className="button primary block w-full mt-auto mb-4"
              onClick={() => {
                setSubmitting(true);
                buyCarCreateOrder({ request_type: 'special', budget }).then(
                  ({ data }) => {
                    setSubmitting(false);
                    setOrder(data.order);
                    setStep(4);
                  },
                  ({ response }) => {
                    setSubmitting(false);
                    toast.error(response.data.message);
                  },
                );
              }}
            >
              مرحله بعدی
            </button>

            <button
              type="button"
              className="button cancel small block w-full"
              onClick={() => {
                setBudgetModalVisible(false);
              }}
            >
              انصراف
            </button>
          </div>
        </Modal>
      )}
    </form>
  );
};

export default RequestType;
